import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Logout,
  Home,
  AssignmentTurnedIn,
  Assignment,
  AccountBalance,
  ArrowBack,
  AccountCircle,
  Business,
  Apartment,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ViiluLogo } from './ViiluLogo';
import { useAuth } from '../modules/auth';
import { useTenant } from '../contexts/TenantContext';
import { MeHostProvider, useMeHost } from '../contexts/MeHostContext';

const DRAWER_WIDTH = 255;

/**
 * MeLayout — visual chrome for /me/* pages.
 *
 * Same look as the tenant Layout (left drawer + top AppBar + avatar menu) but
 * deliberately *not* the tenant Layout — /me aggregates across tenants and
 * mixing in tenant-scoped sidebar items (Invoicing/CRM/…) is confusing.
 *
 * Host-company picker lives in the AppBar; child pages read scope via useMeHost().
 */
export default function MeLayout() {
  return (
    <MeHostProvider>
      <MeLayoutChrome />
    </MeHostProvider>
  );
}

function MeLayoutChrome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { currentTenant } = useTenant();
  const { hostId, setHostId, hosts } = useMeHost();
  const { t } = useTranslation();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const items = [
    { key: 'home', label: t('me.nav.desktop', 'My desktop'), icon: <Home fontSize="small" />, path: '/me' },
    { key: 'all-companies', label: t('open_items.title', 'Accounting firm'), icon: <AccountBalance fontSize="small" />, path: '/me/all-companies' },
    { key: 'companies', label: t('me.nav.companies', 'My companies'), icon: <Apartment fontSize="small" />, path: '/me/companies' },
    { key: 'approvals', label: t('me.nav.approvals', 'My approvals'), icon: <AssignmentTurnedIn fontSize="small" />, path: '/me/approvals' },
    { key: 'tasks', label: t('me.nav.tasks', 'My tasks'), icon: <Assignment fontSize="small" />, path: '/me/tasks' },
  ];
  const isActive = (path) => {
    if (path === '/me') return location.pathname === '/me';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const backToApp = () => {
    setUserMenuAnchor(null);
    const slug = currentTenant?.slug;
    navigate(slug ? `/app/${slug}/dashboard` : '/app');
  };

  const handleUserSettings = () => {
    setUserMenuAnchor(null);
    const slug = currentTenant?.slug;
    navigate(slug ? `/app/${slug}/user-settings` : '/app');
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('me.app_bar.title', 'My desktop')}
          </Typography>

          {hosts.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 240, mr: 2 }}>
              <InputLabel id="me-host-select">{t('me.host_company', 'Host company')}</InputLabel>
              <Select
                labelId="me-host-select"
                label={t('me.host_company', 'Host company')}
                value={hostId}
                onChange={(e) => setHostId(e.target.value)}
              >
                {hosts.map((h) => (
                  <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <IconButton color="inherit" onClick={(e) => setUserMenuAnchor(e.currentTarget)} size="large">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.email?.[0]?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 1, borderRadius: 2 } } }}
      >
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            {user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.displayName || user?.email}
            </Typography>
            {user?.email && user?.displayName && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleUserSettings} sx={{ px: 3, py: 1.5, gap: 2 }}>
          <AccountCircle fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2">{t('nav.user_settings', 'User settings')}</Typography>
        </MenuItem>
        <MenuItem onClick={backToApp} sx={{ px: 3, py: 1.5, gap: 2 }}>
          {currentTenant ? (
            <Business fontSize="small" sx={{ color: 'text.secondary' }} />
          ) : (
            <ArrowBack fontSize="small" sx={{ color: 'text.secondary' }} />
          )}
          <Typography variant="body2">
            {currentTenant
              ? t('me.back_to_tenant', 'Back to {{name}}', { name: currentTenant.display_name || currentTenant.name })
              : t('me.back_to_app', 'Back to app')}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ px: 3, py: 1.5, gap: 2 }}>
          <Logout fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2">{t('nav.logout', 'Logout')}</Typography>
        </MenuItem>
      </Menu>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/me')}>
            <ViiluLogo />
          </Box>
        </Toolbar>
        <List>
          {items.map((item) => (
            <ListItemButton
              key={item.key}
              selected={isActive(item.path)}
              onClick={() => navigate(item.path)}
              sx={{ px: 2, py: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, minHeight: 0, p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

// Re-export for backward compatibility
export { useMeHost } from '../contexts/MeHostContext';
